import type { AttributeKind } from "../utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@mdi/react";
import { mdiTagPlusOutline } from "@mdi/js";
import { Button } from "@acdc2/ui/components/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@acdc2/ui/components/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@acdc2/ui/components/command";

type Props = {
  onSelect: (attributeKind: AttributeKind) => void;
};

export function AttributeCreationButton({ onSelect }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const select = async (attributeKind: AttributeKind) => {
    setOpen(false);
    onSelect(attributeKind);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="flex-1"
        >
          <Icon path={mdiTagPlusOutline} size="1rem" />
          <span>{t("AttributeCreationButton.label")}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <Command>
          <CommandInput
            placeholder={t("AttributeCreationButton.placeholder")}
          />
          <CommandList>
            <CommandEmpty>{t("AttributeCreationButton.empty")}</CommandEmpty>

            <CommandGroup
              heading={t("AttributeCreationButton.groups.text.title")}
            >
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.text.title"),
                  t(
                    "AttributeCreationButton.groups.text.items.headingLevel.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.text.items.headingLevel.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("HeaderLevelAttribute")}
              >
                {t(
                  "AttributeCreationButton.groups.text.items.headingLevel.label",
                )}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.text.title"),
                  t("AttributeCreationButton.groups.text.items.text.label"),
                  ...t(
                    "AttributeCreationButton.groups.text.items.text.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("TextAttribute")}
              >
                {t("AttributeCreationButton.groups.text.items.text.label")}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.text.title"),
                  t(
                    "AttributeCreationButton.groups.text.items.expression.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.text.items.expression.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("ExpressionAttribute")}
              >
                {t(
                  "AttributeCreationButton.groups.text.items.expression.label",
                )}
              </CommandItem>
            </CommandGroup>

            <CommandGroup
              heading={t("AttributeCreationButton.groups.lists.title")}
            >
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.lists.title"),
                  t("AttributeCreationButton.groups.lists.items.list.label"),
                  ...t(
                    "AttributeCreationButton.groups.lists.items.list.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("ListAttribute")}
              >
                {t("AttributeCreationButton.groups.lists.items.list.label")}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.lists.title"),
                  t(
                    "AttributeCreationButton.groups.lists.items.listItem.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.lists.items.listItem.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("ListItemAttribute")}
              >
                {t("AttributeCreationButton.groups.lists.items.listItem.label")}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.lists.title"),
                  t(
                    "AttributeCreationButton.groups.lists.items.listItemLabel.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.lists.items.listItemLabel.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("ListItemLabelAttribute")}
              >
                {t(
                  "AttributeCreationButton.groups.lists.items.listItemLabel.label",
                )}
              </CommandItem>
            </CommandGroup>

            <CommandGroup
              heading={t("AttributeCreationButton.groups.media.title")}
            >
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.media.title"),
                  t("AttributeCreationButton.groups.media.items.image.label"),
                  ...t(
                    "AttributeCreationButton.groups.media.items.image.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("ImageAttribute")}
              >
                {t("AttributeCreationButton.groups.media.items.image.label")}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.media.title"),
                  t("AttributeCreationButton.groups.media.items.figure.label"),
                  ...t(
                    "AttributeCreationButton.groups.media.items.figure.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("FigureAttribute")}
              >
                {t("AttributeCreationButton.groups.media.items.figure.label")}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.media.title"),
                  t("AttributeCreationButton.groups.media.items.caption.label"),
                  ...t(
                    "AttributeCreationButton.groups.media.items.caption.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("CaptionAttribute")}
              >
                {t("AttributeCreationButton.groups.media.items.caption.label")}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.media.title"),
                  t(
                    "AttributeCreationButton.groups.media.items.alternativeText.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.media.items.alternativeText.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("AlternativeTextAttribute")}
              >
                {t(
                  "AttributeCreationButton.groups.media.items.alternativeText.label",
                )}
              </CommandItem>
            </CommandGroup>

            <CommandGroup
              heading={t("AttributeCreationButton.groups.tables.title")}
            >
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.tables.title"),
                  t("AttributeCreationButton.groups.tables.items.table.label"),
                  ...t(
                    "AttributeCreationButton.groups.tables.items.table.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("TableAttribute")}
              >
                {t("AttributeCreationButton.groups.tables.items.table.label")}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.tables.title"),
                  t(
                    "AttributeCreationButton.groups.tables.items.tableCell.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.tables.items.tableCell.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("TableCellAttribute")}
              >
                {t(
                  "AttributeCreationButton.groups.tables.items.tableCell.label",
                )}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.tables.title"),
                  t(
                    "AttributeCreationButton.groups.tables.items.tableHeaderCell.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.tables.items.tableHeaderCell.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("TableHeaderCellAttribute")}
              >
                {t(
                  "AttributeCreationButton.groups.tables.items.tableHeaderCell.label",
                )}
              </CommandItem>
            </CommandGroup>

            <CommandGroup
              heading={t("AttributeCreationButton.groups.decorative.title")}
            >
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.decorative.title"),
                  t(
                    "AttributeCreationButton.groups.decorative.items.excludeReadingOrder.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.decorative.items.excludeReadingOrder.label",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("ExcludeReadingOrderAttribute")}
              >
                {t(
                  "AttributeCreationButton.groups.decorative.items.excludeReadingOrder.label",
                )}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.decorative.title"),
                  t(
                    "AttributeCreationButton.groups.decorative.items.paginationArtifact.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.decorative.items.paginationArtifact.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("PaginationArtifactAttribute")}
              >
                {t(
                  "AttributeCreationButton.groups.decorative.items.paginationArtifact.label",
                )}
              </CommandItem>
            </CommandGroup>

            <CommandGroup
              heading={t("AttributeCreationButton.groups.toc.title")}
            >
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.toc.title"),
                  t(
                    "AttributeCreationButton.groups.toc.items.tableOfContents.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.toc.items.tableOfContents.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("TableOfContentsAttribute")}
              >
                {t(
                  "AttributeCreationButton.groups.toc.items.tableOfContents.label",
                )}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.toc.title"),
                  t(
                    "AttributeCreationButton.groups.toc.items.tableOfContentsHeading.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.toc.items.tableOfContentsHeading.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("TableOfContentsHeadingAttribute")}
              >
                {t(
                  "AttributeCreationButton.groups.toc.items.tableOfContentsHeading.label",
                )}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.toc.title"),
                  t(
                    "AttributeCreationButton.groups.toc.items.tableOfContentsItem.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.toc.items.tableOfContentsItem.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("TableOfContentsItemAttribute")}
              >
                {t(
                  "AttributeCreationButton.groups.toc.items.tableOfContentsItem.label",
                )}
              </CommandItem>
              <CommandItem
                keywords={[
                  t("AttributeCreationButton.groups.toc.title"),
                  t(
                    "AttributeCreationButton.groups.toc.items.tableOfContentsPageNumber.label",
                  ),
                  ...t(
                    "AttributeCreationButton.groups.toc.items.tableOfContentsPageNumber.keywords",
                    { returnObjects: true },
                  ),
                ]}
                onSelect={() => select("TableOfContentsPageNumberAttribute")}
              >
                {t(
                  "AttributeCreationButton.groups.toc.items.tableOfContentsPageNumber.label",
                )}
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
